import React, { useEffect, useState, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  Button,
  ConfirmDelete,
  SecondaryButton
} from '../styles/TableStyles';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';
import { mapColumnLabels } from '../styles/ColumnUtils';

const initialFormState = {
  Commodity: '',
  AvgCartonPrice: '',
  StdCartonCost: '',
  NetWtChile: '',
  NetWtDomestic: '',
  PackingCharge: '',
  Pallets: '',
  ProfitPerBag: '',
  MarketingChg: '',
};

const rawColumns = Object.keys(initialFormState);

const visibleColumns = mapColumnLabels(rawColumns).map((col) => {
  if (['AvgCartonPrice', 'StdCartonCost', 'PackingCharge', 'ProfitPerBag', 'MarketingChg'].includes(col.key)) {
    return { ...col, format: 'currency' };
  }
  return col;
});

const CommoditiesPage = () => {
  const [commodities, setCommodities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCommodities();
  }, []);

  const fetchCommodities = () => {
    authAxios.get('/api/commodities')
      .then(res => setCommodities(res.data))
      .catch(err => console.error('Error fetching commodities:', err));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      if (isEditing) {
        await authAxios.put(`/api/commodities/${editingId}`, formData);
      } else {
        await authAxios.post('/api/commodities', formData);
      }
      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchCommodities();
      savedToast('Commodity');
    } catch (err) {
      console.error('Error submitting form:', err);
      errorToast();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setFormData(row);
    setShowForm(false);
    setIsEditing(true);
    setEditingId(row.UniqueID);
  };

  const handleDelete = async (id) => {
    try {
      await authAxios.delete(`/api/commodities/${id}`);
      fetchCommodities();
      deletedToast('Commodity');
    } catch (err) {
      console.error('Error deleting commodity:', err);
      errorToast('Error, unable to delete');
    }
  };

  const sortedData = useMemo(() => {
    const sortable = [...commodities];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }

        return sortConfig.direction === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }
    return sortable;
  }, [commodities, sortConfig]);

  const filteredData = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    return sortedData.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [searchQuery, sortedData]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <StandardTableLayout
      title="Commodities"
      showForm={showForm}
      submitting={submitting}
      onToggleForm={() => {
        setShowForm(!showForm);
        setIsEditing(false);
        setFormData(initialFormState);
      }}
      onSubmit={handleSubmit}
      editingId={editingId}
      columns={visibleColumns}
      formData={formData}
      handleChange={handleChange}
      rows={filteredData}
      sortConfig={sortConfig}
      onSort={handleSort}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      renderCell={(col, data, handleChange) => {
        // Non-edit mode: render plain text with transparent background
        if (!handleChange) {
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {String(data?.[col] ?? '')}
            </span>
          );
        }
        // Editing: render inputs
        return (
          <Input
            type="text"
            name={col}
            value={data[col]}
            onChange={handleChange}
          />
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData(initialFormState);
              }}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <ConfirmDelete onConfirm={() => handleDelete(row.UniqueID)} />
          </>
        )
      )}
    />
  );
};

export default CommoditiesPage;